import { useEffect, useMemo, useState } from "react";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { DocumentExportPreviewModal } from "@/components/document-builder/DocumentExportPreviewModal";
import { DocumentFillModal } from "@/components/document-builder/DocumentFillModal";
import { listDocAssets } from "@/lib/document/docAssetsClient";
import type { DocAsset } from "@/lib/document/assetTypes";
import type { OrgFormBridgeFillContext } from "@/lib/document-builder/bridge/orgFormBridgeClient";
import type { DocVariableValues } from "@/lib/document-builder/variables/variableFill";
import { saveOrgFormInstanceValues } from "@/lib/opportunity/orgFormInstanceClient";

type Props = {
  instanceId: string;
  context: OrgFormBridgeFillContext;
  useRepContact: boolean;
  onClose: () => void;
  onSaved: () => void;
};

/** Document Builder fill + export for a bridged org form instance (Phase B). */
export function OrgFormDocBridgeFillModal(props: Props) {
  const { instanceId, context, useRepContact, onClose, onSaved } = props;
  const org = useOrgSettings();
  const [assets, setAssets] = useState<DocAsset[]>([]);
  const [fillOpen, setFillOpen] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [values, setValues] = useState<DocVariableValues>(context.variable_values);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const r = await listDocAssets({ signMedia: true });
      if (r.ok) setAssets(r.data.assets);
    })();
  }, []);

  const blocks = useMemo(() => context.blocks, [context.blocks]);

  async function persist(nextValues: DocVariableValues, status: "draft" | "saved") {
    setBusy(true);
    setError(null);
    const r = await saveOrgFormInstanceValues({
      id: instanceId,
      doc_variable_values: nextValues,
      status,
      use_rep_form_contact: useRepContact,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return false;
    }
    onSaved();
    return true;
  }

  return (
    <>
      <DocumentFillModal
        open={fillOpen && !exportOpen}
        documentName={context.document_name}
        variables={context.variables}
        blocks={blocks}
        initialValues={values}
        onClose={onClose}
        onProceed={(next) => {
          setValues(next);
          setFillOpen(false);
          setExportOpen(true);
        }}
      />

      <DocumentExportPreviewModal
        open={exportOpen}
        documentId={context.document_id}
        versionId={context.version_id}
        documentName={context.document_name}
        versionNumber={context.version_number}
        blocks={blocks}
        assets={assets}
        filledValues={values}
        onClose={() => {
          setExportOpen(false);
          onClose();
        }}
      />

      {error ? (
        <div className="fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 rounded-lg bg-red-600 px-4 py-2 text-sm text-white">
          {error}
        </div>
      ) : null}

      {exportOpen ? (
        <div className="fixed bottom-4 right-4 z-[60] flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void persist(values, "draft")}
            className="rounded-lg border border-white/30 bg-black/70 px-3 py-1.5 text-sm text-white"
          >
            Save draft
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void persist(values, "saved")}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white"
          >
            Mark saved
          </button>
        </div>
      ) : null}

      <p className="sr-only">
        Bridged to Document Builder — org branding {org.brandName}
      </p>
    </>
  );
}
